import { TableRowSideSheetForm } from "../../../../common/tables/view/TableRowSideSheetForm";
import React, {
    ChangeEvent,
    FC,
    PropsWithChildren,
    useMemo,
} from "react";
import { PLPRCreateInput } from "../../../../../../common/inputs/ProductListingToPayoutRuleInput";
import { PayoutRule, CostPayoutModifier } from "../../../../../../common/models";
import {
    Button,
    PlusIcon,
    Portal,
    SelectField,
    TextInputField,
} from "evergreen-ui";

interface Props {
    isShown: boolean;
    plprInput: Partial<PLPRCreateInput>;
    payoutRules: PayoutRule[];
    costPayoutModifiers: CostPayoutModifier[];
    mutationLoading: boolean;
    mutationCalled: boolean;
    mutationError: boolean;
    hideFloatingButton?: boolean;
    onInputChanged: (plprInput: Partial<PLPRCreateInput>) => void;
    onDismiss: () => void;
    onOpen: () => void;
    onSubmit: () => void;
}

export const PLPRTableRowSideSheetForm: FC<Props> = ({
    isShown,
    plprInput,
    payoutRules,
    costPayoutModifiers,
    mutationLoading,
    mutationCalled,
    mutationError,
    hideFloatingButton,
    onInputChanged,
    onOpen,
    onDismiss,
    onSubmit,
}: PropsWithChildren<Props>) => {

    const onChange = ({ target: { value, name, type }}: ChangeEvent<HTMLInputElement>) => {
        onInputChanged({
            ...plprInput,
            [name]: (type === "number") ? (value === "" ? undefined : parseFloat(value)) : value,
        });
    };

    const onSelectChange = ({ target: { value, name }}: ChangeEvent<HTMLSelectElement>) => {
        onInputChanged({
            ...plprInput,
            [name]: value || undefined,
        });
    };

    const isReadyToSubmit = useMemo((): boolean => {
        return !!(
            plprInput.payoutRuleId &&
            plprInput.startDate
        );
    }, [plprInput]);

    return (
        <>
            {!hideFloatingButton && (
                <Portal>
                    <Button
                        minWidth={"160px"}
                        iconBefore={PlusIcon}
                        size={"large"}
                        appearance={"primary"}
                        position={"absolute"}
                        bottom={30}
                        right={280}
                        onClick={onOpen}
                        boxShadow={"0 4px 8px rgba(0, 0, 0, 0.2)"}
                    >
                        Assign Payout Rule
                    </Button>
                </Portal>
            )}
            <TableRowSideSheetForm
                isShown={isShown}
                headerTitle={"Assign Payout Rule"}
                mutationLoading={mutationLoading}
                mutationCalled={mutationCalled}
                mutationError={mutationError}
                canSubmit={isReadyToSubmit}
                onDismiss={onDismiss}
                onSubmit={onSubmit}
            >
                <SelectField
                    name={"payoutRuleId"}
                    label={"Payout Rule"}
                    value={plprInput.payoutRuleId ?? ""}
                    required={true}
                    isInvalid={!plprInput.payoutRuleId}
                    onChange={onSelectChange}
                >
                    <option value={""}>Select a payout rule</option>
                    {payoutRules.map(rule => (
                        <option key={rule.id} value={rule.id}>
                            {rule.name} ({rule.type})
                        </option>
                    ))}
                </SelectField>
                <TextInputField
                    name={"startDate"}
                    label={"Start Date"}
                    type={"datetime-local"}
                    value={(plprInput.startDate as unknown as string) ?? ""}
                    required={true}
                    maxWidth={"300px"}
                    isInvalid={!plprInput.startDate}
                    onChange={onChange}
                />
                <TextInputField
                    name={"startThreshold"}
                    label={"Start Threshold"}
                    type={"number"}
                    value={plprInput.startThreshold ?? ""}
                    required={false}
                    onChange={onChange}
                />
                <SelectField
                    name={"costPayoutModifierId"}
                    label={"Cost Payout Modifier"}
                    value={plprInput.costPayoutModifierId ?? ""}
                    onChange={onSelectChange}
                >
                    <option value={""}>None</option>
                    {costPayoutModifiers.map(cpm => (
                        <option key={cpm.id} value={cpm.id}>
                            {cpm.name}
                        </option>
                    ))}
                </SelectField>
            </TableRowSideSheetForm>
        </>
    );
};
